import { createWebHistory, createRouter } from 'vue-router';
import logoutCleanup from './services/logout-cleanup';
import Login from './components/Login.vue';
import Register from './components/Register.vue';
import store from './store';
import { hasRoutePermissions } from './services/route-permissions';
import SchemeRegister from './components/schemes/SchemesRegister.vue';
import RequestResetCode from './components/RequestResetCode.vue';
import ResetPassword from './components/ResetPassword.vue';

import Dashboard from './components/dashboard/Dashboard.vue';

import SchemeEditRouter from './components/schemes/edit/SchemesEditRouter.vue';
import SchemeEditAdvanced from './components/schemes/edit/SchemesEditAdvanced.vue';
import SchemeEditDetails from './components/schemes/edit/SchemesEditDetails.vue';
import SchemeEditRegistration from './components/schemes/edit/SchemesEditRegistration.vue';
import SchemeAssociateWithDispute from './components/schemes/edit/SchemesAssociateWithDispute.vue';

import SchemeContactRouter from './components/schemes/edit/contacts/SchemesEditContactsRouter.vue';
import SchemeContactList from './components/schemes/edit/contacts/SchemesEditContactsList.vue';
import SchemeContactCreate from './components/schemes/edit/contacts/SchemesEditContactsCreate.vue';
import SchemeContactEdit from './components/schemes/edit/contacts/SchemesEditContactsEdit.vue';

import SchemeViewNoteRouter from './components/schemes/notes/SchemesViewNotesRouter.vue';
import SchemeViewNoteList from './components/schemes/notes/SchemesViewNotesList.vue';

import SchemeNoteRouter from './components/schemes/edit/notes/SchemesEditNotesRouter.vue';
import SchemeNoteList from './components/schemes/edit/notes/SchemesEditNotesList.vue';
import SchemeNoteCreate from './components/schemes/edit/notes/SchemesEditNotesCreate.vue';
import SchemeNoteEdit from './components/schemes/edit/notes/SchemesEditNotesEdit.vue';

import SchemeBusinessNotesRouter from './components/schemeBusiness/notes/SchemeBusinessNotesRouter.vue';
import SchemeBusinessNotesList from './components/schemeBusiness/notes/SchemeBusinessNotesList.vue';
import SchemeBusinessNotesCreate from './components/schemeBusiness/notes/SchemeBusinessNotesCreate.vue';
import SchemeBusinessNotesEdit from './components/schemeBusiness/notes/SchemeBusinessNotesEdit.vue';

import SchemeBusinessWatchlist from './components/schemeBusiness/SchemeBusinessWatchlist.vue';

import SchemeTradeRouter from './components/schemes/edit/trades/SchemesEditTradesRouter.vue';
import SchemeTradeList from './components/schemes/edit/trades/SchemesEditTradesList.vue';
import SchemeTradeCreate from './components/schemes/edit/trades/SchemesEditTradesCreate.vue';
import SchemeTradeEdit from './components/schemes/edit/trades/SchemesEditTradesEdit.vue';

import TradeRouter from './components/trades/TradesRouter.vue';
import TradeList from './components/trades/TradesList.vue';
import TradeCreate from './components/trades/TradesCreate.vue';
import TradeEdit from './components/trades/TradesEdit.vue';

import ListApplications from './components/applications/ApplicationsList.vue';
import ViewApplication from './components/applications/ApplicationsView.vue';
import EditApplication from './components/applications/ApplicationEdit.vue';
import ApplicationTradingStandards from './components/applications/ApplicationsTradingStandards.vue';

import TradingStandardsAdminView from './components/tradingStandards/TradingStandardsAdminView.vue';
import TradingStandardsYearView from './components/tradingStandards/TradingStandardsYearView.vue';

import TradingStandardsFeedbackView from './components/tradingStandardsFeedback/TradingStandardsFeedbackView.vue';
import TradingStandardsFeedbackEditView from './components/tradingStandardsFeedback/TradingStandardsFeedbackEditView.vue';

import RegistrationBillingList from './components/registrationBilling/RegistrationBillingList.vue';

import LicenceNumberList from './components/licenceNumber/LicenceNumberList.vue';
import LicenceView from './components/licenceNumber/View/LicenceNumberViewInformation.vue';
import LicenceRouter from './components/licenceNumber/View/LicenceNumberViewRouter.vue';
import LicenceLinkedBusinesses from './components/licenceNumber/View/LicenceNumberViewLinkedBusinesses.vue';
import LicenceTrades from './components/licenceNumber/View/LicenceNumberViewTrades.vue';
import LicenceCompliance from './components/licenceNumber/View/LicenceNumberViewCompliance.vue';
import LicenceRetrofitAccount from './components/licenceNumber/View/LicenceNumberViewRetrofitAccount.vue';
import LicenceRetrofitTopup from './components/licenceNumber/View/LicenceNumberViewRetrofitTopup.vue';
import LicenceRetrofitRefund from './components/licenceNumber/View/LicenceNumberViewRetrofitRefund.vue';
import LicenceNumberViewAssociatedTmlns from './components/licenceNumber/View/LicenceNumberViewAssociatedTmlns.vue';
import RemediationDateHistoryTable from './components/licenceNumber/RemediationDateHistoryTable.vue';

import LicenceGroupList from './components/licenceGroup/LicenceGroupList.vue';

import SchemeBusinessContacts from './components/schemeBusiness/SchemeBusinessContacts.vue';
import SchemeBusinessOverview from './components/schemeBusiness/SchemeBusinessOverview.vue';
import SchemeBusinessRouter from './components/schemeBusiness/SchemeBusinessRouter.vue';
import SchemeBusinessTrades from './components/schemeBusiness/SchemeBusinessTradesAndRegistrations.vue';
import SchemeBusinessEmails from './components/schemeBusiness/SchemeBusinessEmails.vue';
import SchemeBusinessActivityLogs from './components/schemeBusiness/SchemeBusinessActivityLogs.vue';
import SchemeBusinessFormattedActivityLogs from './components/schemeBusiness/SchemeBusinessFormattedActivityLogs.vue';
import SchemeBusinessCeaseToTrade from './components/schemeBusiness/SchemeBusinessCeaseToTrade.vue';
import SchemeBusinessVerificationList from './components/schemeBusiness/SchemeBusinessVerificationList.vue';

import PendingLicenceActionList from './components/pendingLicenceAction/PendingLicenceActionList.vue';

import SoftwareProvidersList from './components/softwareProviders/SoftwareProvidersList.vue';
import SoftwareProviderView from './components/softwareProviders/SoftwareProviderView.vue';
import SoftwareProviderCreate from './components/softwareProviders/SoftwareProviderCreate.vue';

import SchemeUserRouter from './components/schemes/edit/user/UserRouter.vue';
import SchemeUserList from './components/schemes/edit/user/UserList.vue';
import SchemeUserView from './components/schemes/edit/user/UserView.vue';
import { readonly } from 'vue';

const BusinessList = () => import('./components/businesses/BusinessList.vue');
const BusinessWatchlistList = () => import('./components/watchlist/BusinessWatchlistList.vue');

const FundersRouter = () => import('./components/funders/FundersRouter.vue');
const FundersList = () => import('./components/funders/FundersList.vue');
const FundersRegister = () => import('./components/funders/FundersRegister.vue');
const FundersEditRouter = () => import('./components/funders/edit/FundersEditRouter.vue');
const FundersEditDetails = () => import('./components/funders/edit/FundersEditDetails.vue');
const FundersEditUsers = () => import('./components/funders/edit/FundersEditUsers.vue');
const FundersEditUsage = () => import('./components/funders/edit/FundersEditUsage.vue');
const FundersEditActivityLog = () => import('./components/funders/edit/FundersEditActivityLog.vue');
const FundersEditAdvanced = () => import('./components/funders/edit/FundersEditAdvanced.vue');
const FundersEditUser = () => import('./components/funders/edit/users/FundersEditUser.vue');
const FundersEditUserInvite = () =>
  import('./components/funders/edit/users/FundersEditUserInvite.vue');


const GuaranteeProvidersRouter = () => import('./components/guaranteeProviders/GuaranteeProvidersRouter.vue');
const GuaranteeProvidersList = () => import('./components/guaranteeProviders/GuaranteeProvidersList.vue');
const GuaranteeProvidersRegister = () => import('./components/guaranteeProviders/GuaranteeProvidersRegister.vue');
const GuaranteeProvidersEditRouter = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditRouter.vue');
const GuaranteeProvidersEditDetails = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditDetails.vue');
const GuaranteeProvidersEditActivityLog = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditActivityLog.vue');
const GuaranteeProvidersEditAdvanced = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditAdvanced.vue');
const GuaranteeProvidersEditUsage = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditUsage.vue');
const GuaranteeProvidersEditRetrofitAccount = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditRetrofitAccount.vue');
const GuaranteeProvidersEditLodgementCheck = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersEditLodgementCheck.vue');
const GuaranteeProvidersRequests = () => import('./components/guaranteeProviders/edit/GuaranteeProvidersRequests.vue');

const PropertyCheckerRouter = () => import('./components/propertyChecker/PropertyCheckerRouter.vue');
const PropertyCheckerUser = () => import('./components/propertyChecker/users/PropertyCheckerEditUser.vue');
const PropertyCheckerUserInvite = () => import('./components/propertyChecker/users/PropertyCheckerEditUserInvite.vue');
const PropertyCheckerUsers = () => import('./components/propertyChecker/PropertyCheckerEditUsers.vue');

// lazy-loaded
const SchemeList = () => import('./components/schemes/SchemesList.vue');
const SchemeViewRouter = () => import('./components/schemes/SchemeViewRouter.vue');
const SchemeViewOverview = () => import('./components/schemes/SchemeViewOverview.vue');
const SchemeViewDetails = () => import('./components/schemes/SchemeViewDetails.vue');
const SchemeViewTrades = () => import('./components/schemes/SchemeViewTrades.vue');
const SchemeViewStatistics = () => import('./components/schemes/SchemeViewStatistics.vue');

const SiteSettingsOverview = () => import('./components/site/SiteSettingsOverview.vue');
const SiteSettingsRouter = () => import('./components/site/SiteSettingsRouter.vue');
const EditSiteSettings = () => import('./components/site/EditSiteSettings.vue');

const AuditQuestionsOverview = () =>
  import('./components/auditQuestions/AuditQuestionsOverview.vue');
const AuditQuestionsImport = () => import('./components/auditQuestions/AuditQuestionsImport.vue');
const AuditQuestionsRouter = () => import('./components/auditQuestions/AuditQuestionsRouter.vue');
const EditAuditQuestions = () => import('./components/auditQuestions/EditAuditQuestions.vue');
const EditSingleAuditQuestion = () =>
  import('./components/auditQuestions/EditSingleAuditQuestion.vue');
const NewAuditQuestionSet = () => import('./components/auditQuestions/NewAuditQuestionSet.vue');
const AuditQuestionsVersionList = () =>
  import('./components/auditQuestions/AuditQuestionsVersionList.vue');

const BackOfficeUserList = () => import('./components/backofficeUsers/BackOfficeUserList.vue');
const BackOfficeUserEdit = () => import('./components/backofficeUsers/BackOfficeUserEdit.vue');
const BackOfficeUserInvite = () => import('./components/backofficeUsers/BackOfficeUserInvite.vue');
const BackOfficeUserView = () => import('./components/backofficeUsers/BackOfficeUserView.vue');
const BackOfficeUserAccountEvents = () => import('./components/backofficeUsers/UserAccountEventsView.vue');
const BackOfficeUserDelete = () => import('./components/backofficeUsers/BackOfficeUserDelete.vue');

const SchemeUsageOverview = () => import('./components/schemeUsage/SchemeUsageOverview.vue');
const SchemeUsageRouter = () => import('./components/schemeUsage/SchemeUsageRouter.vue');

const BlockedEmailsOverview = () => import('./components/blockedEmails/BlockedEmailsOverview.vue');
const BlockedEmailsRouter = () => import('./components/blockedEmails/BlockedEmailsRouter.vue');

const Insights = () => import('./components/insights/Insights.vue');

const MatrixTypesOverview = () => import('./components/matrixTypes/MatrixTypesOverview.vue');
const MatrixTypesRouter = () => import('./components/matrixTypes/MatrixTypesRouter.vue');
const MatrixTypesViewOverview = () =>
  import('./components/matrixTypes/view/MatrixTypesViewOverview.vue');
const MatrixTypesViewRouter = () =>
  import('./components/matrixTypes/view/MatrixTypesViewRouter.vue');
const MatrixTypesViewCosts = () => import('./components/matrixTypes/view/MatrixTypesViewCosts.vue');
const MatrixTypesViewRequiredDocuments = () =>
  import('./components/matrixTypes/view/MatrixTypesViewRequiredDocuments.vue');
const MatrixTypesViewRestrictedTrades = () =>
  import('./components/matrixTypes/view/MatrixTypesViewRestrictedTrades.vue');
const MatrixTypesViewLocalAuthority = () =>
  import('./components/matrixTypes/view/MatrixTypesViewLocalAuthority.vue');
const MatrixTypesViewQuestions = () =>
  import('./components/matrixTypes/view/MatrixTypesViewQuestions.vue');
const MatrixTypesViewFinancialProducts = () =>
  import('./components/matrixTypes/view/MatrixTypesViewFinancialProducts.vue');

const MeasuresOverview = () => import('./components/measures/MeasuresOverview.vue');
const MeasuresRouter = () => import('./components/measures/MeasuresRouter.vue');
const MeasuresViewRouter = () => import('./components/measures/view/MeasuresViewRouter.vue');
const MeasuresViewOverview = () => import('./components/measures/view/MeasuresViewOverview.vue');

const FinancialProductsOverview = () => import('./components/financialProducts/FinancialProductsOverview.vue');
const FinancialProductsRouter = () => import('./components/financialProducts/FinancialProductsRouter.vue');
const FinancialProductsViewRouter = () => import('./components/financialProducts/view/FinancialProductsViewRouter.vue');
const FinancialProductsViewOverview = () => import('./components/financialProducts/view/FinancialProductsViewOverview.vue');

const BespokeAuditTypesOverview = () => import('./components/bespokeAuditTypes/BespokeAuditTypesOverview.vue');
const BespokeAuditTypesRouter = () => import('./components/bespokeAuditTypes/BespokeAuditTypesRouter.vue');
const BespokeAuditTypesViewRouter = () => import('./components/bespokeAuditTypes/view/BespokeAuditTypesViewRouter.vue');
const BespokeAuditTypesViewOverview = () => import('./components/bespokeAuditTypes/view/BespokeAuditTypesViewOverview.vue');

const BrandmisuseList = () => import('./components/brandmisuse/BrandmisuseList.vue');
const BrandmisusePreCheck = () => import('./components/brandmisuse/BrandmisusePreCheck.vue');
const BrandmisuseOverview = () => import('./components/brandmisuse/BrandmisuseOverview.vue');
const BrandmisuseView = () => import('./components/brandmisuse/BrandmisuseView.vue');
const BrandmisuseEvidenceRouter = () => import('./components/brandmisuse/BrandmisuseEvidenceRouter.vue');
const BrandmisuseAssociationRouter = () => import('./components/brandmisuse/BrandmisuseAssociationRouter.vue');
const BrandmisuseTradingStandardsView = () => import('./components/brandmisuse/BrandmisuseTradingStandardsView.vue');

const BrandMisuseIntelList = () => import('./components/brandmisuseIntel/BrandMisuseIntelList.vue');
const BrandMisuseIntelOverview = () => import('./components/brandmisuseIntel/BrandMisuseIntelOverview.vue');
const BrandMisuseIntelInfoView = () => import('./components/brandmisuseIntel/BrandMisuseIntelInfoView.vue');
const BrandMisuseIntelTradingStandardsView = () => import('./components/brandmisuseIntel/BrandMisuseIntelTradingStandardsView.vue');

const BusinessReports = () => import('./components/businesses/reports/BusinessReports.vue');
const BusinessReportsList = () => import('./components/businesses/reports/BusinessReportsList.vue');
const BusinessReportsNew = () => import('./components/businesses/reports/BusinessReportsNew.vue');

const BusinessRegistrationType = () =>
  import('./components/businessRegistration/BusinessRegistrationType.vue');
const BusinessRegistrationLimitedCompany = () =>
  import('./components/businessRegistration/BusinessRegistrationLimitedCompany.vue');
const BusinessRegistrationSoleTrader = () =>
  import('./components/businessRegistration/BusinessRegistrationSoleTrader.vue');

const EnergySuppliersRouter = () => import('./components/energySuppliers/EnergySuppliersRouter.vue');
const EnergySuppliersOverview = () => import('./components/energySuppliers/EnergySuppliersOverview.vue');
const EnergySuppliersViewRouter = () => import('./components/energySuppliers/view/EnergySuppliersViewRouter.vue');
const EnergySuppliersViewOverview = () => import('./components/energySuppliers/view/EnergySuppliersViewOverview.vue');
const EnergySuppliersViewNotifications = () => import('./components/energySuppliers/view/EnergySuppliersViewNotifications.vue');

const HeatServiceRouter = () => import('./components/heatService/HeatServiceRouter.vue');
const HeatServiceRequestLog = () => import('./components/heatService/HeatServiceRequestLog.vue');
const EEICLAApiKeysRouter = () => import('./components/eeiclaApiKeys/EEICLAApiKeysRouter.vue');
const EEICLAApiKeysList = () => import('./components/eeiclaApiKeys/EEICLAApiKeysList.vue');
const EEICLAApiKeysCreate = () => import('./components/eeiclaApiKeys/EEICLAApiKeysCreate.vue');
const EEICLAApiKeysView = () => import('./components/eeiclaApiKeys/EEICLAApiKeysView.vue');

const DisputesAuthView = () => import('./components/disputes/DisputesAuthView.vue');
const DisputesProjectView = () => import('./components/disputes/DisputesProjectView.vue');
const ComplaintTriage = () => import('./components/complaintTriage/ComplaintTriage.vue');
const ComplaintCase = () => import('./components/complaint/ComplaintViewEdit.vue');

const AccountView = () => import('./components/account/AccountView.vue');

const DisputesList = () => import('./components/disputes/DisputesList.vue');
const DisputeView = () => import('./components/disputes/dispute/DisputeView.vue');
const DisputeNotesRouter = () => import('./components/disputes/dispute/notes/DisputeNotesRouter.vue');
const DisputeAssociations = () => import('./components/disputes/dispute/DisputeAssociations.vue');
const DisputeOverview = () => import('./components/disputes/dispute/DisputeOverview.vue');
const DisputeNotesList = () => import('./components/disputes/dispute/notes/DisputeNotesList.vue');
const DisputeNotesCreate = () => import('./components/disputes/dispute/notes/DisputeNotesCreate.vue');
const DisputeNotesEdit = () => import('./components/disputes/dispute/notes/DisputeNotesEdit.vue');
const DisputeEvidenceRouter = () => import('./components/disputes/dispute/evidence/DisputeEvidenceRouter.vue');
const DisputeEvidenceList = () => import('./components/disputes/dispute/evidence/DisputeEvidenceList.vue');
const DisputeEvidenceCreate = () => import('./components/disputes/dispute/evidence/DisputeEvidenceCreate.vue');
const DisputeEvidenceEdit = () => import('./components/disputes/dispute/evidence/DisputeEvidenceEdit.vue');
const DisputeSourceEdit = () => import('./components/disputes/dispute/source/DisputeSourceEdit.vue');
const DisputeBrandMisuseEdit = () => import('./components/disputes/dispute/brand-misuse/DisputeBrandMisuseEdit.vue');
const DisputeHawthorneEdit = () => import('./components/disputes/dispute/hawthorne/DisputeHawthorneEdit.vue');
const DisputeAssociationRouter = () => import('./components/disputes/dispute/association/DisputeAssociationRouter.vue');
const DisputeAssociationList = () => import('./components/disputes/dispute/association/DisputeAssociationList.vue');
const DisputeAssociationCreate = () => import('./components/disputes/dispute/association/DisputeAssociationCreate.vue');
const DisputeAssociationEdit = () => import('./components/disputes/dispute/association/DisputeAssociationEdit.vue');
const DisputeActivityLogRouter = () => import('./components/disputes/dispute/activity-log/DisputeActivityLogRouter.vue');
const DisputePreCheck = () => import('./components/disputes/dispute/DisputePreCheck.vue');
const DisputeResolutionOmbudsmanFields = () => import('./components/disputes/dispute/resolution-ombudsman/DisputeResolutionOmbudsmanFields.vue');
const GuaranteePolicyClaimList = () => import('./components/disputes/dispute/guarantee-claims/GuaranteePolicyClaimList.vue');
const GuaranteePolicyClaimCreate = () => import('./components/disputes/dispute/guarantee-claims/GuaranteePolicyClaimCreate.vue');
const GuaranteePolicyClaimEdit = () => import('./components/disputes/dispute/guarantee-claims/GuaranteePolicyClaimEdit.vue');
const GuaranteePolicyClaimView = () => import('./components/disputes/dispute/guarantee-claims/GuaranteePolicyClaimView.vue');
const DisputeMerge = () => import('./components/disputes/DisputesMerge.vue');
const DisputeLaison = () => import('./components/disputes/dispute/DisputeLiaison.vue');

const GuaranteeClaimOverview = () => import('./components/disputes/GuaranteeClaimOverview.vue');

const UserRoleGroupsOverview = () => import('./components/userRoleGroups/UserRoleGroupsOverview.vue');
const UserRoleGroupsRouter = () => import('./components/userRoleGroups/UserRoleGroupsRouter.vue');
const UserRoleGroupsViewRouter = () => import('./components/userRoleGroups/view/UserRoleGroupsViewRouter.vue');
const UserRoleGroupsViewOverview = () => import('./components/userRoleGroups/view/UserRoleGroupsViewOverview.vue');
const UserRoleGroupsViewActivityLog = () => import('./components/userRoleGroups/view/UserRoleGroupsViewActivityLog.vue');
const UserRoleGroupsViewDangerZone = () => import('./components/userRoleGroups/view/UserRoleGroupsViewDangerZone.vue');
const UserRoleGroupsViewUsers = () => import('./components/userRoleGroups/view/UserRoleGroupsViewUsers.vue');

const DataSubjectAccessRequestList = () => import('./components/dataSubjectAccessRequest/DataSubjectAccessRequestList.vue');
const DataSubjectAccessRequestView = () => import('./components/dataSubjectAccessRequest/DataSubjectAccessRequestView.vue');
const DataSubjectAccessRequestOverview = () => import('./components/dataSubjectAccessRequest/DataSubjectAccessRequestOverview.vue');
const DataSubjectAccessRequestActivityLog = () => import('./components/dataSubjectAccessRequest/DataSubjectAccessRequestActivityLog.vue');
const DataSubjectAccessRequestNotesRouter = () => import('./components/dataSubjectAccessRequest/notes/DataSubjectAccessRequestNotesRouter.vue');
const DataSubjectAccessRequestNotesView = () => import('./components/dataSubjectAccessRequest/notes/DataSubjectAccessRequestNotesView.vue');
const DataSubjectAccessRequestNotesList = () => import('./components/dataSubjectAccessRequest/notes/DataSubjectAccessRequestNotesList.vue');
const DataSubjectAccessRequestEvidenceRouter = () => import('./components/dataSubjectAccessRequest/evidence/DataSubjectAccessRequestEvidenceRouter.vue');
const DataSubjectAccessRequestEvidenceList = () => import('./components/dataSubjectAccessRequest/evidence/DataSubjectAccessRequestEvidenceList.vue');
const DataSubjectAccessRequestEvidenceCreate = () => import('./components/dataSubjectAccessRequest/evidence/DataSubjectAccessRequestEvidenceCreate.vue');
const DataSubjectAccessRequestEvidenceEdit = () => import('./components/dataSubjectAccessRequest/evidence/DataSubjectAccessRequestEvidenceEdit.vue');
const DataSubjectAccessRequestAssociationRouter = () => import('./components/dataSubjectAccessRequest/association/DataSubjectAccessRequestAssociationRouter.vue');
const DataSubjectAccessRequestAssociationList = () => import('./components/dataSubjectAccessRequest/association/DataSubjectAccessRequestAssociationList.vue');
const DataSubjectAccessRequestAssociationCreate = () => import('./components/dataSubjectAccessRequest/association/DataSubjectAccessRequestAssociationCreate.vue');
const DataSubjectAccessRequestAssociationEdit = () => import('./components/dataSubjectAccessRequest/association/DataSubjectAccessRequestAssociationEdit.vue');

const BusinessRegistrationList = () => import('./components/businessRegistration/BusinessRegistrationList.vue');
const BusinessRegistrationViewRouter = () =>
  import('./components/businessRegistration/BusinessRegistrationViewRouter.vue');
const BusinessRegistrationViewInformation = () =>
  import('./components/businessRegistration/BusinessRegistrationViewInformation.vue');
const BusinessRegistrationPeople = () =>
  import('./components/businessRegistration/businessRegistrationPerson/BusinessRegistrationPeople.vue');
const BusinessRegistrationSchemeBusinesses = () =>
  import('./components/businessRegistration/BusinessRegistrationSchemeBusinesses.vue');
const BusinessRegistrationEdit = () =>
  import('./components/businessRegistration/BusinessRegistrationEdit.vue');
const BusinessRegistrationPersonCreate = () =>
  import('./components/businessRegistration/businessRegistrationPerson/BusinessRegistrationPersonCreate.vue');
const BusinessRegistrationPersonRouter = () =>
  import('./components/businessRegistration/businessRegistrationPerson/BusinessRegistrationPersonRouter.vue');
const BusinessRegistrationPersonEdit = () =>
  import('./components/businessRegistration/businessRegistrationPerson/BusinessRegistrationPersonEdit.vue');
const BusinessRegistrationPeriodsContainer = () =>
  import('./components/businessRegistration/businessRegistrationPeriod/BusinessRegistrationPeriodsContainer.vue');
const BusinessRegistrationCompaniesHouseRouter = () =>
  import('./components/businessRegistration/BusinessRegistrationCompaniesHouseRouter.vue');
const BusinessRegistrationCompaniesHouse = () =>
  import('./components/businessRegistration/BusinessRegistrationCompaniesHouse.vue');
const BusinessRegistrationVerificationActivityLogs = () =>
  import('./components/businessRegistration/BusinessRegistrationVerificationActivityLogs.vue');


const PersonRegistrationList = () => import('./components/personRegistration/PersonRegistrationList.vue');
const PersonRegistrationView = () => import('./components/personRegistration/PersonRegistrationView.vue');
const PersonRegistrationOverview = () => import('./components/personRegistration/PersonRegistrationOverview.vue');
const PersonBusinessRegistrations = () =>
  import('./components/personRegistration/PersonBusinessRegistrations.vue');
const PersonDocumentRouter = () => import('./components/personRegistration/personDocuments/PersonDocumentRouter.vue');
const PersonDocumentList = () => import('./components/personRegistration/personDocuments/PersonDocumentList.vue');
const PersonDocumentCreate = () => import('./components/personRegistration/personDocuments/PersonDocumentCreate.vue');
const PersonDocumentEdit = () => import('./components/personRegistration/personDocuments/PersonDocumentEdit.vue');

const ComplaintList = () => import('./components/complaint/ComplaintList.vue');

const ComplaintTriageList = () => import('./components/complaintTriage/ComplaintTriageList.vue');

const performLogoutWithLoginRedirect = () => {
  logoutCleanup();
};

const canViewPersonRegistration = () => {
  const account = JSON.parse(localStorage.getItem('account') || 'null');
  return Boolean(account?.roles?.includes('PersonRegistrationViewer'));
};

const canEditBusinessRegistration = () => {
  const account = JSON.parse(localStorage.getItem('account') || 'null');
  return Boolean(account?.roles?.includes('BusinessRegistrationEditor'));
};

const routes = [
  {
    path: '/',
    name: 'home',
    component: Dashboard,
    meta: {
      title: 'Dashboard',
      schemes: false,
    },
  },
  {
    path: '/register',
    component: Register,
    meta: {
      title: 'Register',
    },
  },
  {
    path: '/forgot',
    component: RequestResetCode,
    meta: {
      title: 'Forgot',
    },
  },
  {
    path: '/reset-password',
    component: ResetPassword,
    meta: {
      title: 'Reset',
    },
  },
  {
    path: '/account',
    component: AccountView,
    meta: {
      title: 'Account',
      account: true,
    },
  },
  {
    path: '/backoffice-users',
    name: 'backofficeUsers',
    component: BackOfficeUserList, // lazy-loaded
    meta: {
      title: 'BackOffice Users',
      users: true,
    },
  },
  {
    path: '/backoffice-users/invite',
    name: 'backofficeUserInvite',
    component: BackOfficeUserInvite, // lazy-loaded
    meta: {
      title: 'BackOffice Invite User',
      users: true,
    },
  },
  {
    path: '/backoffice-user/:backofficeUserId',
    name: 'userView',
    component: BackOfficeUserView, // lazy-loaded
    meta: {
      title: 'View BackOffice User',
      users: true,
    },
  },
  {
    path: '/backoffice-user/:backofficeUserId/events',
    name: 'userAccountEvents',
    component: BackOfficeUserAccountEvents, // lazy-loaded
    meta: {
      title: 'User Account Events',
      users: true,
    },
  },
  {
    path: '/backoffice-user/:backofficeUserId/archive',
    name: 'userArchive',
    component: BackOfficeUserDelete, // lazy-loaded
    meta: {
      title: 'Archive BackOffice User',
      users: true,
    },
  },
  {
    path: '/backoffice-user/:backofficeUserId/edit',
    name: 'userEdit',
    component: BackOfficeUserEdit, // lazy-loaded
    meta: {
      title: 'Edit BackOffice User',
      users: true,
    },
  },
  {
    path: '/site-settings/',
    component: SiteSettingsRouter,
    meta: {
      title: 'Settings',
      siteSettings: true,
    },
    children: [
      {
        path: '',
        component: SiteSettingsOverview,
      },
      {
        path: 'edit',
        component: EditSiteSettings,
      },
      {
        path: 'details',
        component: SchemeViewDetails, // lazy-loaded
      },
    ],
  },
  {
    path: '/audit-questions/',
    component: AuditQuestionsRouter,
    meta: {
      title: 'Audit Questions',
      auditQuestions: true,
    },
    children: [
      {
        path: '',
        component: AuditQuestionsOverview,
      },
      {
        path: 'import',
        component: AuditQuestionsImport,
      },
      {
        path: 'new',
        component: NewAuditQuestionSet,
      },
      {
        path: ':auditQuestionSetId/draft',
        name: 'audit-question-set',
        component: EditAuditQuestions,
      },
      {
        path: ':auditQuestionSetId/draft/:questionId',
        name: 'audit-question-set-question',
        component: EditSingleAuditQuestion,
      },
      {
        path: ':auditQuestionSetId/review',
        name: 'audit-question-set-review',
        component: EditAuditQuestions,
      },
      {
        path: ':auditQuestionSetId/review/:questionId',
        name: 'audit-question-set-question-review',
        component: EditSingleAuditQuestion,
      },
      {
        path: ':auditQuestionSetId/current',
        name: 'audit-question-set-current',
        component: EditAuditQuestions,
      },
      {
        path: ':auditQuestionSetId/current/:questionId',
        name: 'audit-question-set-question-current',
        component: EditSingleAuditQuestion,
      },
      {
        path: ':auditQuestionSetId/versions',
        name: 'audit-question-set-versions',
        component: AuditQuestionsVersionList,
      },
      {
        path: ':auditQuestionSetId/versions/:version',
        name: 'audit-question-set-version',
        component: EditAuditQuestions,
      },
      {
        path: ':auditQuestionSetId/versions/:version/:questionId',
        name: 'audit-question-set-question-version',
        component: EditSingleAuditQuestion,
      },
    ],
  },
  {
    path: '/matrix-types/',
    component: MatrixTypesRouter,
    meta: {
      title: 'Matrix Types',
      matrixTypes: true,
    },
    children: [
      {
        path: '',
        component: MatrixTypesOverview,
      },
    ],
  },
  {
    path: '/measure-types/',
    component: MeasuresRouter,
    meta: {
      title: 'Measure Types',
      measures: true,
    },
    children: [
      {
        path: '',
        name: 'measures-search',
        component: MeasuresOverview,
      },
      {
        path: ':code',
        component: MeasuresViewRouter,
        children: [
          {
            path: '',
            component: MeasuresViewOverview,
          },
        ],
      },
    ],
  },
  {
    path: '/financial-products/',
    component: FinancialProductsRouter,
    meta: {
      title: 'Financial Products',
      financialProducts: true,
    },
    children: [
      {
        path: '',
        component: FinancialProductsOverview,
      },
      {
        path: ':code',
        component: FinancialProductsViewRouter,
        children: [
          {
            path: '',
            component: FinancialProductsViewOverview,
          },
        ],
      },
    ],
  },
  {
    path: '/bespoke-audit-types/',
    component: BespokeAuditTypesRouter,
    meta: {
      title: 'Bespoke Audit Types',
      bespokeAuditTypes: true,
    },
    children: [
      {
        path: '',
        component: BespokeAuditTypesOverview,
      },
      {
        path: ':type',
        component: BespokeAuditTypesViewRouter,
        children: [
          {
            path: '',
            component: BespokeAuditTypesViewOverview,
          },
        ],
      },
    ],
  },
  {
    path: '/energy-suppliers/',
    component: EnergySuppliersRouter,
    meta: {
      title: 'Energy Suppliers',
      energySuppliers: true,
    },
    children: [
      {
        path: '',
        name: 'energy-suppliers-search',
        component: EnergySuppliersOverview,
      },
      {
        path: ':energySupplierId',
        component: EnergySuppliersViewRouter,
        children: [
          {
            path: '',
            component: EnergySuppliersViewOverview,
          },
          {
            path: 'notifications',
            component: EnergySuppliersViewNotifications,
          },
        ],
      },
    ],
  },
  {
    path: '/heat-service',
    component: HeatServiceRouter,
    meta: {
      title: 'Heat Service',
      heatService: true,
    },
    children: [
      {
        path: '',
        name: 'heat-service-request-log',
        component: HeatServiceRequestLog,
      },
    ],
  },
  {
    path: '/eeicla-api-keys',
    component: EEICLAApiKeysRouter,
    meta: {
      title: 'EEICLA API Keys',
      eeiclaApiKeys: true,
    },
    children: [
      {
        path: '',
        component: EEICLAApiKeysList,
      },
      {
        path: 'create',
        component: EEICLAApiKeysCreate,
        meta: {
          title: 'Create EEICLA API Key',
          eeiclaApiKeys: true,
        },
      },
      {
        path: ':tmApiKey',
        component: EEICLAApiKeysView,
        meta: {
          title: 'EEICLA API Key',
          eeiclaApiKeys: true,
        },
      },
    ],
  },
  {
    path: '/matrix-types/:type',
    component: MatrixTypesViewRouter,
    meta: {
      title: 'Matrix Type',
      matrixTypes: true,
    },
    children: [
      {
        path: '',
        component: MatrixTypesViewOverview, // lazy-loaded
      },
      {
        path: 'costs',
        component: MatrixTypesViewCosts, // lazy-loaded
      },
      {
        path: 'required-documents',
        component: MatrixTypesViewRequiredDocuments, // lazy-loaded
      },
      {
        path: 'restricted-trades',
        component: MatrixTypesViewRestrictedTrades, // lazy-loaded
      },
      {
        path: 'local-authority',
        component: MatrixTypesViewLocalAuthority, // lazy-loaded
      },
      {
        path: 'questions',
        component: MatrixTypesViewQuestions, // lazy-loaded
      },
      {
        path: 'financial-products',
        component: MatrixTypesViewFinancialProducts, // lazy-loaded
      },
    ],
  },
  {
    path: '/scheme-usage/',
    component: SchemeUsageRouter,
    meta: {
      title: 'Scheme Usage',
    },
    children: [
      {
        path: '',
        component: SchemeUsageOverview,
      },
    ],
  },
  {
    path: '/blocked-emails/',
    component: BlockedEmailsRouter,
    meta: {
      title: 'Blocked Emails',
      blockedEmails: true,
    },
    children: [
      {
        path: '',
        component: BlockedEmailsOverview,
      },
    ],
  },
  {
    path: '/login',
    component: Login,
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    beforeEnter: performLogoutWithLoginRedirect,
  },
  {
    path: '/schemes',
    component: SchemeList,
    meta: {
      title: 'Schemes',
      schemes: true,
    },
  },
  {
    path: '/scheme/:schemeId',
    component: SchemeViewRouter,
    meta: {
      title: 'Scheme',
      schemes: true,
    },
    children: [
      {
        path: '',
        component: SchemeViewOverview, // lazy-loaded
      },
      {
        path: 'details',
        component: SchemeViewDetails, // lazy-loaded
      },
      {
        path: 'trades',
        component: SchemeViewTrades, // lazy-loaded
      },
      {
        path: 'statistics',
        component: SchemeViewStatistics, // lazy-loaded
      },
      {
        path: 'notes',
        component: SchemeViewNoteRouter, // lazy-loaded
        children: [
          {
            path: '',
            component: SchemeViewNoteList, // lazy-loaded
          },
        ],
      },
    ],
  },
  {
    path: '/scheme/:schemeId/scheme-user/:backofficeUserId/events',
    name: 'schemeUserAccountEvents',
    component: BackOfficeUserAccountEvents, // lazy-loaded
    meta: {
      title: 'User Account Events',
      schemeUsers: true,
    },
  },
  {
    path: '/scheme/:schemeId/edit',
    name: 'schemeEdit',
    component: SchemeEditRouter, // lazy-loaded
    meta: {
      title: 'Edit Scheme',
      schemes: true,
    },
    children: [
      {
        path: 'advanced',
        component: SchemeEditAdvanced, // lazy-loaded
      },
      {
        path: '',
        component: SchemeEditDetails, // lazy-loaded
        meta: {
          displayActionButtons: true,
        },
      },
      {
        path: 'associate-with-dispute',
        component: SchemeAssociateWithDispute,
      },
      {
        path: 'contacts',
        component: SchemeContactRouter, // lazy-loaded
        children: [
          {
            path: '',
            component: SchemeContactList, // lazy-loaded
          },
          {
            path: 'create',
            component: SchemeContactCreate, // lazy-loaded
          },
          {
            path: 'edit/:contactId',
            component: SchemeContactEdit, // lazy-loaded
          },
        ],
      },
      {
        path: 'notes',
        component: SchemeNoteRouter, // lazy-loaded
        children: [
          {
            path: '',
            component: SchemeNoteList, // lazy-loaded
          },
          {
            path: 'create',
            component: SchemeNoteCreate, // lazy-loaded
          },
          {
            path: 'edit/:noteId',
            component: SchemeNoteEdit, // lazy-loaded
          },
        ],
      },
      {
        path: 'scheme-trades',
        component: SchemeTradeRouter, // lazy-loaded
        children: [
          {
            path: '',
            component: SchemeTradeList, // lazy-loaded
          },
          {
            path: 'create',
            component: SchemeTradeCreate, // lazy-loaded
          },
          {
            path: 'edit/:tradeCode',
            component: SchemeTradeEdit, // lazy-loaded
          },
        ],
      },
      {
        path: 'registration',
        component: SchemeEditRegistration, // lazy-loaded
        meta: {
          displayActionButtons: true,
        },
      },
      {
        path: 'users',
        component: SchemeUserRouter,
        children: [
          {
            path: '',
            component: SchemeUserList,
          },
          {
            path: ':userid',
            component: SchemeUserView,
          },
        ],
      },
    ],
  },
  {
    path: '/trades',
    component: TradeRouter, // lazy-loaded
    children: [
      {
        path: '',
        component: TradeList, // lazy-loaded
        meta: {
          title: 'Trades',
          trades: true,
        },
      },
      {
        path: 'create',
        component: TradeCreate, // lazy-loaded
        meta: {
          title: 'Create Trades',
          trades: true,
        },
      },
      {
        path: 'edit/:tradeId',
        component: TradeEdit, // lazy-loaded
        meta: {
          title: 'Edit Trades',
          trades: true,
        },
      },
    ],
  },
  {
    path: '/schemes/register',
    name: 'scheme-register',
    component: SchemeRegister, // lazy-loaded
    meta: {
      title: 'Register Scheme',
      schemes: true,
    },
  },
  {
    path: '/trades',
    component: TradeList,
    meta: {
      title: 'Trades',
      trades: true,
    },
  },
  {
    path: '/applications',
    component: ListApplications, // lazy-loaded
    meta: {
      title: 'Applications',
      applications: true,
    },
  },
  {
    path: '/verification',
    component: SchemeBusinessVerificationList,
    meta: {
      title: 'Verification',
      verification: true,
    },
  },
  {
    path: '/applications/:applicationRef',
    component: ViewApplication,
    meta: {
      title: 'View Application',
      applications: true,
    },
  },
  {
    path: '/applications/:applicationRef/edit',
    component: EditApplication,
    meta: {
      title: 'Edit Application',
      applications: true,
    },
  },
  {
    path: '/applications/:applicationRef/trading-standards',
    component: ApplicationTradingStandards,
    meta: {
      title: 'View Application',
      applications: true,
    },
  },
  {
    path: '/tradingstandards',
    component: TradingStandardsAdminView,
    meta: {
      title: 'View Trading Standards',
      tradingStandardsadmin: true,
    },
  },
  {
    path: '/tradingstandards/:tmln/:renewalYear',
    component: TradingStandardsYearView,
    meta: {
      title: 'Edit Trading Standards Application',
      tradingStandardsadmin: true,
    },
  },
  {
    path: '/trading-standards-feedback',
    component: TradingStandardsFeedbackView,
    meta: {
      title: 'View Trading Standards',
      tradingStandardsfeedback: true,
    },
  },
  {
    path: '/trading-standards-feedback/:tmln/:renewalYear',
    component: TradingStandardsFeedbackEditView,
    meta: {
      title: 'Edit Trading Standards Application',
      tradingStandardsfeedback: true,
    },
  },
  {
    path: '/registrations',
    component: RegistrationBillingList, // lazy-loaded
    meta: {
      title: 'Registration Billing',
      registrations: true,
    },
  },
  {
    path: '/tmlns',
    component: LicenceNumberList, // lazy-loaded
    meta: {
      title: 'Licence Numbers',
      tmlns: true,
    },
  },
  {
    path: '/licence-groups',
    component: LicenceGroupList, // lazy-loaded
    meta: {
      title: 'Licence Groups',
      licenceGroups: true,
    },
  },
  {
    path: '/person-registration',
    component: PersonRegistrationList,
    beforeEnter: () => (canViewPersonRegistration() ? true : '/'),
    meta: {
      title: 'Person Registration',
      personRegistration: true,
    },
  },
  {
    path: '/person-registration/:trustmarkPersonId/edit',
    component: PersonRegistrationOverview,
    meta: { readonly: false, personRegistration: true },
    children: [
      { path: '', component: PersonRegistrationView },
      { path: 'business-registrations', component: PersonBusinessRegistrations },
      {
        path: 'key-documents',
        component: PersonDocumentRouter,
        children: [
          {
            path: '',
            component: PersonDocumentList,
          },
          {
            path: 'create',
            component: PersonDocumentCreate,
          },
          {
            path: 'edit/:trustmarkPersonDocumentId',
            component: PersonDocumentEdit,
          }
        ]
      },
    ]
  },
  {
    path: '/person-registration/:trustmarkPersonId',
    component: PersonRegistrationOverview,
    meta: { readonly: true, personRegistration: true },
    children: [
       { path: '', component: PersonRegistrationView },
      { path: 'business-registrations', component: PersonBusinessRegistrations },
      {
        path: 'key-documents',
        component: PersonDocumentRouter,
        children: [
          {
            path: '',
            component: PersonDocumentList,
          },
        ]
      },
    ]
  },
  {
    path: '/tmlns/users/:backofficeUserId/events',
    name: 'retrofitUserAccountEvents',
    component: BackOfficeUserAccountEvents, // lazy-loaded
    meta: {
      title: 'User Account Events',
      tmlns: true,
    },
  },
  {
    path: '/tmlns/:tmBusinessId/',
    name: 'scheme-edit',
    component: LicenceRouter, // lazy-loaded
    meta: {
      title: 'License',
      tmlns: true,
    },
    children: [
      {
        path: '',
        component: LicenceView, // lazy-loaded
      },
      {
        path: 'Linked-businesses',
        component: LicenceLinkedBusinesses, // lazy-loaded
      },
      {
        path: 'trades',
        component: LicenceTrades, // lazy-loaded
      },
      {
        name: 'retrofit-account',
        path: 'retrofit-account',
        component: LicenceRetrofitAccount,
      },
      {
        name: 'compliance',
        path: 'compliance',
        component: LicenceCompliance,
      },
      {
        path: 'retrofit-topup',
        component: LicenceRetrofitTopup,
      },
      {
        path: 'retrofit-refund',
        component: LicenceRetrofitRefund,
      },
      {
        path: 'associated-tmlns',
        component: LicenceNumberViewAssociatedTmlns,
      },
      {
        path: 'remediation-date-history',
        component: RemediationDateHistoryTable,
      },
    ],
  },
  {
    path: '/businesses',
    name: 'businesses',
    component: BusinessList, // lazy-loaded
    meta: {
      title: 'Businesses',
      business: true,
    },
    children: [
      {
        path: 'reports',
        component: BusinessReports,
        children: [
          {
            path: '',
            component: BusinessReportsList, // lazy-loaded
            meta: {
              exports: true,
              list: true,
            },
          },
          {
            path: 'new',
            component: BusinessReportsNew, // lazy-loaded
            meta: {
              exports: true,
              new: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: '/watchlist',
    name: 'watchlist',
    component: BusinessWatchlistList, // lazy-loaded
    meta: {
      title: 'Watchlist',
      watchlist: true,
    },
  },
  {
    path: '/funders',
    name: 'funders',
    component: FundersRouter, // lazy-loaded
    meta: {
      title: 'Funders',
      funders: true,
    },
    children: [
      {
        path: '',
        component: FundersList,
      },
      {
        path: 'register',
        component: FundersRegister,
      },
      {
        path: ':funderId',
        component: FundersEditRouter,
        children: [
          {
            path: '',
            redirect: { name: 'funderDetails' },
          },
          {
            path: 'details',
            name: 'funderDetails',
            component: FundersEditDetails,
            meta: {
              displayActionButtons: true,
            },
          },
          {
            path: 'advanced',
            component: FundersEditAdvanced,
          },
          {
            path: 'users',
            component: FundersEditUsers,
          },
          {
            path: 'users/:accountId',
            component: FundersEditUser,
          },
          {
            path: 'users/invite',
            component: FundersEditUserInvite,
          },
          {
            path: 'usage',
            component: FundersEditUsage,
          },
          {
            path: 'activity-log',
            component: FundersEditActivityLog,
          },
        ],
      },
    ],
  },
  {
    path: '/funders/:funderId/users/:backofficeUserId/events',
    name: 'fundersUserAccountEvents',
    component: BackOfficeUserAccountEvents, // lazy-loaded
    meta: {
      title: 'User Account Events',
      funders: true,
    },
  },
  {
    path: '/property-checker',
    name: 'property-checker',
    component: PropertyCheckerRouter,
    meta: {
      propertyCheckerUsers: true,
    },
    children: [
      {
        path: '',
        redirect: { name: 'propertyCheckerUsers' },
      },
      {
        path: 'users',
        name: 'propertyCheckerUsers',
        component: PropertyCheckerUsers,
      },
      {
        path: 'users/invite',
        component: PropertyCheckerUserInvite,
      },
      {
        path: 'users/:accountId',
        component: PropertyCheckerUser,
      },
    ],
  },
  {
    path: '/property-checker/users/:backofficeUserId/events',
    name: 'propertyCheckerUserAccountEvents',
    component: BackOfficeUserAccountEvents, // lazy-loaded
    meta: {
      title: 'User Account Events',
      propertyCheckerUsers: true,
    },
  },
  {
    path: '/scheme-business/:schemeId/:schemeBusinessId/',
    name: 'Scheme Business',
    component: SchemeBusinessRouter, // lazy-loaded
    meta: {
      title: 'Scheme Business',
      business: true,
    },
    children: [
      {
        path: '',
        component: SchemeBusinessOverview, // lazy-loaded
      },
      {
        path: 'trades',
        component: SchemeBusinessTrades, // lazy-loaded
      },
      {
        path: 'contacts',
        component: SchemeBusinessContacts, // lazy-loaded
      },
      {
        path: 'emails',
        component: SchemeBusinessEmails, // lazy-loaded
      },
      {
        path: 'formatted-activity-logs',
        component: SchemeBusinessFormattedActivityLogs, // lazy-loaded
      },
      {
        path: 'raw-activity-logs',
        component: SchemeBusinessActivityLogs, // lazy-loaded
      },
      {
        path: 'watchlist',
        component: SchemeBusinessWatchlist, // lazy-loaded
      },
      {
        path: 'cease-to-trade',
        component: SchemeBusinessCeaseToTrade,
      },
      {
        path: 'notes',
        component: SchemeBusinessNotesRouter, // lazy-loaded
        children: [
          {
            path: '',
            component: SchemeBusinessNotesList, // lazy-loaded
          },
          {
            path: 'create',
            component: SchemeBusinessNotesCreate, // lazy-loaded
          },
          {
            path: 'edit/:noteId',
            component: SchemeBusinessNotesEdit, // lazy-loaded
          },
        ],
      },
    ],
  },
  {
    path: '/pending-licences',
    component: PendingLicenceActionList, // lazy-loaded
    meta: {
      pendingLicences: true,
    },
  },
  {
    path: '/pending-licences/:schemeId/:schemeBusinessId/',
    name: 'Pending Licence Action',
    component: SchemeBusinessRouter, // lazy-loaded
    meta: {
      title: 'Pending Licence Action',
      pendingLicences: true,
    },
    children: [
      {
        path: '',
        component: SchemeBusinessOverview, // lazy-loaded
      },
      {
        path: 'trades',
        component: SchemeBusinessTrades, // lazy-loaded
      },
      {
        path: 'contacts',
        component: SchemeBusinessContacts, // lazy-loaded
      },
      {
        path: 'emails',
        component: SchemeBusinessEmails, // lazy-loaded
      },
      {
        path: 'formatted-activity-logs',
        component: SchemeBusinessFormattedActivityLogs, // lazy-loaded
      },
      {
        path: 'raw-activity-logs',
        component: SchemeBusinessActivityLogs, // lazy-loaded
      },
      {
        path: 'notes',
        component: SchemeBusinessNotesRouter, // lazy-loaded
        children: [
          {
            path: '',
            component: SchemeBusinessNotesList, // lazy-loaded
          },
          {
            path: 'create',
            component: SchemeBusinessNotesCreate, // lazy-loaded
          },
          {
            path: 'edit/:noteId',
            component: SchemeBusinessNotesEdit, // lazy-loaded
          },
        ],
      },
    ],
  },
  {
    path: '/software-providers',
    component: SoftwareProvidersList, // lazy-loaded
    meta: {
      title: 'Software Providers',
      softwareProviders: true,
    },
  },
  {
    path: '/software-provider/:softwareProviderId',
    component: SoftwareProviderView,
    meta: {
      title: 'View Software Provider',
      softwareProviders: true,
    },
  },
  {
    path: '/register-software-provider',
    component: SoftwareProviderCreate,
    meta: {
      title: 'Register Software Provider',
      softwareProviders: true,
    },
  },
  {
    path: '/insights',
    component: Insights,
    meta: {
      title: 'Insights',
      insights: true,
    },
  },
  {
    path: '/auth/retrofit',
    component: DisputesAuthView,
  },
  {
    path: '/complaints-triage',
    component: ComplaintTriageList,
    meta: {
      title: 'Complaints Triage Search',
      complaintsTriage: true,
    },
  },
  {
    path: '/complaint-triage/:complaintTriageId',
    component: ComplaintTriage,
    meta: {
      title: 'Complaint Triage',
      readonly: true,
    },
  },
  {
    path: '/complaint-triage/:complaintTriageId/edit',
    component: ComplaintTriage,
    meta: {
      title: 'Complaint Triage',
      readonly: false,
    },
  },
  {
    path: '/complaint/:complaintNumber',
    component: ComplaintCase,
    meta: {
      title: 'Complaint',
      complaint: true,
      permissions: ['ComplaintViewer', 'ComplaintEditor'],
      readonly: true,
    },
  },
  {
    path: '/complaint/:complaintNumber/edit',
    component: ComplaintCase,
    meta: {
      title: 'Complaint',
      complaint: true,
      permissions: ['ComplaintViewer', 'ComplaintEditor'],
      readonly: false,
    },
  },
  {
    path: '/dispute',
    component: DisputesProjectView,
    meta: {
      title: 'Disputes',
      disputes: true,
    },
  },
  {
    path: '/guarantee-providers',
    name: 'guaranteeProviders',
    component: GuaranteeProvidersRouter, // lazy-loaded
    meta: {
      title: 'Guarantee Providers',
      guaranteeProviders: true,
    },
    children: [
      {
        path: '',
        component: GuaranteeProvidersList,
      },
      {
        path: 'register',
        component: GuaranteeProvidersRegister,
      },
      {
        path: ':guaranteeProviderId',
        component: GuaranteeProvidersEditRouter,
        children: [
          {
            path: '',
            redirect: { name: 'guaranteeProviderDetails' },
          },
          {
            path: 'details',
            name: 'guaranteeProviderDetails',
            component: GuaranteeProvidersEditDetails,
            meta: {
              displayActionButtons: true,
            },
          },
          {
            path: 'retrofit-account',
            component: GuaranteeProvidersEditRetrofitAccount,
          },
          {
            path: 'lodgement-check',
            component: GuaranteeProvidersEditLodgementCheck,
            meta: {
              displayActionButtons: true,
            },
          },
          {
            path: 'advanced',
            component: GuaranteeProvidersEditAdvanced,
          },
          {
            path: 'activity-log',
            component: GuaranteeProvidersEditActivityLog,
          },
          {
            path: 'usage',
            component: GuaranteeProvidersEditUsage,
          },
          {
            path: 'requests',
            component: GuaranteeProvidersRequests,
          },
        ],
      },
    ],
  },
  {
    path: '/user-role-groups',
    component: UserRoleGroupsRouter,
    meta: {
      title: 'User Role Groups',
      userRoleGroups: true,
    },
    children: [
      {
        path: '',
        component: UserRoleGroupsOverview,
      },
      {
        path: ':userRoleGroupId',
        component: UserRoleGroupsViewRouter,
        children: [
          { path: '', component: UserRoleGroupsViewOverview },
          { path: 'users', component: UserRoleGroupsViewUsers },
          { path: 'activity-log', component: UserRoleGroupsViewActivityLog },
          { path: 'danger-zone', component: UserRoleGroupsViewDangerZone },
        ],
      },
      {
        path: 'create',
        component: UserRoleGroupsViewRouter,
        children: [
          { path: '', component: UserRoleGroupsViewOverview },
        ],
      },
    ],
  },
  {
    path: '/disputes',
    component: DisputesList,
    meta: {
      title: 'Disputes',
      disputes: true,
    },
    children: [
      {
        path: 'pre-check',
        component: DisputePreCheck,
      },
      {
        path: 'merge-disputes',
        component: DisputeMerge,
      },
      {
        path: ':disputeId',
        component: DisputeOverview,
        meta: {
          readonly: true,
        },
        children: [
          {
            path: '',
            component: DisputeView,
          },
          {
            path: 'notes',
            component: DisputeNotesRouter,
            children: [
              {
                path: '',
                component: DisputeNotesList, // lazy-loaded
              },
            ],
          },
          {
            path: 'evidence',
            component: DisputeEvidenceRouter,
            children: [
              {
                path: '',
                component: DisputeEvidenceList,
              },
            ],
          },
          {
            path: 'dispute-laison',
            component: DisputeLaison,
          },
          {
            path: 'associations',
            component: DisputeAssociationRouter,
            children: [
              {
                path: '',
                component: DisputeAssociationList,
              },
            ],
          },
          {
            path: 'associations',
            component: DisputeAssociations,
          },
          {
            path: 'source',
            component: DisputeSourceEdit,
          },
          {
            path: 'brand-misuse',
            component: DisputeBrandMisuseEdit,
          },
          {
            path: 'hawthorne',
            component: DisputeHawthorneEdit,
          },
          {
            path: 'activity-log',
            component: DisputeActivityLogRouter,
          },
          {
            path: 'resolution-ombudsman',
            component: DisputeResolutionOmbudsmanFields,
          },
          {
            path: 'guarantee-claims',
            component: GuaranteePolicyClaimList,
          },
          {
            path: 'guarantee-claims/:guaranteePolicyClaimId',
            component: GuaranteePolicyClaimView,
          },
        ],
      },
      {
        path: ':disputeId/edit',
        component: DisputeOverview,
        meta: {
          readonly: false,
        },
        children: [
          {
            path: '',
            component: DisputeView,
          },
          {
            path: 'notes',
            component: DisputeNotesRouter,
            children: [
              {
                path: '',
                component: DisputeNotesList, // lazy-loaded
              },
              {
                path: 'create',
                component: DisputeNotesCreate, // lazy-loaded
              },
              {
                path: 'edit/:noteId',
                component: DisputeNotesEdit, // lazy-loaded
              },
            ],
          },
          {
            path: 'evidence',
            component: DisputeEvidenceRouter,
            children: [
              {
                path: '',
                component: DisputeEvidenceList,
              },
              {
                path: 'create',
                component: DisputeEvidenceCreate,
              },
              {
                path: 'edit/:evidenceId',
                component: DisputeEvidenceEdit,
              },
            ],
          },
          {
            path: 'dispute-laison',
            component: DisputeLaison,
          },
          {
            path: 'associations',
            component: DisputeAssociationRouter,
            children: [
              {
                path: '',
                component: DisputeAssociationList,
              },
              {
                path: 'create',
                component: DisputeAssociationCreate,
              },
              {
                path: 'edit/:associationId',
                component: DisputeAssociationEdit,
              },
            ],
          },
          {
            path: 'associations',
            component: DisputeAssociations,
          },
          {
            path: 'source',
            component: DisputeSourceEdit,
          },
          {
            path: 'brand-misuse',
            component: DisputeBrandMisuseEdit,
          },
          {
            path: 'hawthorne',
            component: DisputeHawthorneEdit,
          },
          {
            path: 'activity-log',
            component: DisputeActivityLogRouter,
          },
          {
            path: 'resolution-ombudsman',
            component: DisputeResolutionOmbudsmanFields,
          },
          {
            path: 'guarantee-claims',
            component: GuaranteePolicyClaimList,
          },
          {
            path: 'guarantee-claims/create',
            component: GuaranteePolicyClaimCreate,
          },
          {
            path: 'guarantee-claims/edit/:guaranteePolicyClaimId',
            component: GuaranteePolicyClaimEdit,
          },
          {
            path: 'guarantee-claims/:guaranteePolicyClaimId',
            component: GuaranteePolicyClaimView,
          },
        ],
      },
    ],
  },
  {
    path: '/guarantee-claims',
    component: GuaranteeClaimOverview,
    meta: {
      title: 'GuaranteeClaims',
      guaranteeClaims: true,
    }
  },
  {
    path: '/dsar',
    component: DataSubjectAccessRequestList,
    meta: {
      title: 'DataSubjectAccessRequest',
      dataSubjectAccessRequest: true,
    },
  },
  {
    path: '/business-registration',
    component: BusinessRegistrationList,
    meta: {
      title: 'BusinessRegistration',
      businessRegistration: true,
    },
  },
  {
    path: '/dsar/:dsarId/edit',
    component: DataSubjectAccessRequestOverview,
    meta: { readonly: false, dataSubjectAccessRequest: true, },
    children: [
      { path: '', component: DataSubjectAccessRequestView },
      {
        path: 'notes',
        component: DataSubjectAccessRequestNotesRouter,
        children: [
          {
            path: '',
            component: DataSubjectAccessRequestNotesList, // lazy-loaded
          },
          {
            path: ':dsarInternalNoteId',
            component: DataSubjectAccessRequestNotesView
          },
          {
            path: 'edit/:dsarInternalNoteId',
            component: DataSubjectAccessRequestNotesView
          }
        ],
      },
      {
        path: 'evidence',
        component: DataSubjectAccessRequestEvidenceRouter,
        children: [
          {
            path: '',
            component: DataSubjectAccessRequestEvidenceList,
          },
          {
            path: 'create',
            component: DataSubjectAccessRequestEvidenceCreate,
          },
          {
            path: 'edit/:dsarEvidenceId',
            component: DataSubjectAccessRequestEvidenceEdit,
          }
        ]
      },
      {
        path: 'associations',
        component: DataSubjectAccessRequestAssociationRouter,
        children: [
          {
            path: '',
            component: DataSubjectAccessRequestAssociationList,
          },
          {
            path: 'create',
            component: DataSubjectAccessRequestAssociationCreate,
          },
          {
            path: 'edit/:dsarAssociationId',
            component: DataSubjectAccessRequestAssociationEdit,
          }
        ]
      },
      { path: 'activity-log', component: DataSubjectAccessRequestActivityLog },
    ],
  },
  {
    path: '/dsar/:dsarId',
    component: DataSubjectAccessRequestOverview,
    meta: { readonly: true, dataSubjectAccessRequest: true, },
    children: [
      { path: '', component: DataSubjectAccessRequestView },
      {
        path: 'notes',
        component: DataSubjectAccessRequestNotesRouter,
        children: [
          {
            path: '',
            component: DataSubjectAccessRequestNotesList, // lazy-loaded
          },
        ],
      },
      {
        path: 'evidence',
        component: DataSubjectAccessRequestEvidenceRouter,
        children: [
          {
            path: '',
            component: DataSubjectAccessRequestEvidenceList,
          }
        ]
      },
      {
        path: 'associations',
        component: DataSubjectAccessRequestAssociationRouter,
        children: [
          {
            path: '',
            component: DataSubjectAccessRequestAssociationList,
          }
        ]
      },
      { path: 'activity-log', component: DataSubjectAccessRequestActivityLog },
    ],
  },
  {
    path: '/complaints',
    component: ComplaintList,
    meta: {
      title: 'Complaints',
      complaints: true,
    },
  },
  {
    path: '/brandmisuse',
    component: BrandmisuseList,
    meta: {
      title: 'Brandmisuse',
      brandmisuse: true,
    },
    children: [
      {
        path: 'pre-check',
        component: BrandmisusePreCheck,
      },
      {
        path: ':disputeId',
        component: BrandmisuseOverview,
        meta: {
          readonly: true,
        },
        children: [
          {
            path: '',
            component: BrandmisuseView,
          },
          {
            path: 'evidence',
            component: BrandmisuseEvidenceRouter,
            children: [
              {
                path: '',
                component: DisputeEvidenceList,
                props: { isBrandmisuse: true }
              }
            ]
          },
          {
            path: 'trading-standards',
            component: BrandmisuseTradingStandardsView,
          },
          {
            path: 'notes',
            component: DisputeNotesRouter,
            children: [
              {
                path: '',
                component: DisputeNotesList, // lazy-loaded
                props: { isBrandmisuse: true }
              },
            ],
          },
          {
            path: 'activity-log',
            component: DisputeActivityLogRouter,
            props: { isBrandmisuse: true }
          },
          {
            path: 'associations',
            component: BrandmisuseAssociationRouter,
            children: [
              {
                path: '',
                component: DisputeAssociationList,
              },
            ]
          }
        ]
      },
      {
        path: ':disputeId/edit',
        component: BrandmisuseOverview,
        meta: {
          readonly: false,
        },
        children: [
          {
            path: '',
            component: BrandmisuseView,
          },
          {
            path: 'evidence',
            component: BrandmisuseEvidenceRouter,
            children: [
              {
                path: '',
                component: DisputeEvidenceList,
                props: { isBrandmisuse: true }
              },
              {
                path: 'create',
                component: DisputeEvidenceCreate,
                props: { isBrandmisuse: true }
              },
              {
                path: 'edit/:evidenceId',
                component: DisputeEvidenceEdit,
                props: { isBrandmisuse: true }
              },
            ]
          },
          {
            path: 'trading-standards',
            component: BrandmisuseTradingStandardsView,
          },
          {
            path: 'notes',
            component: DisputeNotesRouter,
            children: [
              {
                path: '',
                component: DisputeNotesList, // lazy-loaded
                props: { isBrandmisuse: true }
              },
              {
                path: 'create',
                component: DisputeNotesCreate, // lazy-loaded
                props: { isBrandmisuse: true }
              },
              {
                path: 'edit/:noteId',
                component: DisputeNotesEdit, // lazy-loaded
                props: { isBrandmisuse: true }
              },
            ],
          },
          {
            path: 'activity-log',
            component: DisputeActivityLogRouter,
            props: { isBrandmisuse: true }
          },
          {
            path: 'associations',
            component: BrandmisuseAssociationRouter,
            children: [
              {
                path: '',
                component: DisputeAssociationList,
              },
              {
                path: 'create',
                component: DisputeAssociationCreate,
                props: { isBrandmisuse: true }
              },
              {
                path: 'edit/:associationId',
                component: DisputeAssociationEdit,
                props: { isBrandmisuse: true }
              },
            ],
          },
        ]
      },
    ]
  },
  {
    path: '/business-registration/create',
    component: BusinessRegistrationType,
    meta: {
      title: 'New Business Registration',
      businessRegistration: true,
    },
  },
  {
    path: '/business-registration/create/limited-company',
    component: BusinessRegistrationLimitedCompany,
    meta: {
      title: 'New Limited Company Registration',
      businessRegistration: true,
    },
  },
  {
    path: '/business-registration/create/sole-trader',
    component: BusinessRegistrationSoleTrader,
    meta: {
      title: 'New Sole Trader Registration',
      businessRegistration: true,
    },
  },
  {
    path: '/business-registration/:businessRegistrationNumber/edit',
    component: BusinessRegistrationViewRouter,
    beforeEnter: () => (canEditBusinessRegistration() ? true : '/business-registration'),
    meta: {
      title: 'Edit Business Registration',
      businessRegistration: true,
      readonly: false,
    },
    children: [
      {
        path: '',
        component: BusinessRegistrationEdit,
      },
      {
        path: 'people',
        component: BusinessRegistrationPersonRouter,
        children: [
          {
            path: '',
            component: BusinessRegistrationPeople,
          },
          {
            path: 'create',
            component: BusinessRegistrationPersonCreate,
          },
          {
            path: ':businessRegistrationPersonId/business-registration-person',
            component: BusinessRegistrationPersonEdit
          }
        ]
      },
      {
        path: 'scheme-businesses',
        component: BusinessRegistrationSchemeBusinesses,
      },
      {
        path: 'periods',
        component: BusinessRegistrationPeriodsContainer,
      },
      {
        path: 'companies-house',
        component: BusinessRegistrationCompaniesHouseRouter,
        children: [
          {
            path: '',
            component: BusinessRegistrationCompaniesHouse,
          },
          {
            path: 'activity-logs',
            component: BusinessRegistrationVerificationActivityLogs,
          },
        ],
      },
    ],
  },
  {
    path: '/business-registration/:businessRegistrationNumber',
    component: BusinessRegistrationViewRouter,
    meta: {
      title: 'Business Registration',
      businessRegistration: true,
      readonly: true,
    },
    children: [
      {
        path: '',
        component: BusinessRegistrationViewInformation,
      },
      {
        path: 'people',
        component: BusinessRegistrationPersonRouter,
        children: [
          {
            path: '',
            component: BusinessRegistrationPeople,
          },
          {
            path: ':businessRegistrationPersonId/business-registration-person',
            component: BusinessRegistrationPersonEdit
          }
        ]
      },
      {
        path: 'scheme-businesses',
        component: BusinessRegistrationSchemeBusinesses,
      },
      {
        path: 'periods',
        component: BusinessRegistrationPeriodsContainer,
      },
      {
        path: 'companies-house',
        component: BusinessRegistrationCompaniesHouseRouter,
        children: [
          {
            path: '',
            component: BusinessRegistrationCompaniesHouse,
          },
          {
            path: 'activity-logs',
            component: BusinessRegistrationVerificationActivityLogs,
          },
        ],
      },
    ],
  },
  {
    path: '/brand-misuse-intel',
    component: BrandMisuseIntelList,
    meta: {
      title: 'Brand Misuse Intel',
      brandmisuseIntel: true,
    },
  },
  {
    path: '/brand-misuse-intel/:disputeId',
    component: BrandMisuseIntelOverview,
    meta: {
      title: 'Brand Misuse Intel',
      brandmisuseIntel: true,
    },
    children: [
      {
        path: '',
        component: BrandMisuseIntelInfoView,
      },
      {
        path: 'trading-standards',
        component: BrandMisuseIntelTradingStandardsView,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const publicPages = ['/login', '/register', '/forgot', '/reset-password', '/auth/retrofit'];
  const authRequired = !publicPages.includes(to.path);
  const isPublicPage = publicPages.includes(to.path);
  const loggedIn = localStorage.getItem('account');
  const account = JSON.parse(loggedIn || 'null');
  // Collapse menu
  store.commit('setSidebarCollapsed', true);
  store.commit('setIsBusy', true, { root: true });

  // Redirect to login if not logged in and attempting to access route that requires permission
  if (authRequired && !loggedIn) {
    next('/login');
  } else if (!hasRoutePermissions(to, account?.roles)) {
    next('/');
  } else if (isPublicPage && loggedIn) {
    localStorage.removeItem('account');
    window.location = to.path;
  } else if (!store.state.auth.appData && loggedIn) {
    store.dispatch('auth/getAppData').then(() => {
      const { roles } = account;
      if (roles.length < 3 && roles.includes('TradingStandardsFeedback')) {
        next('/trading-standards-feedback');
      } else if (roles.length < 3 && roles.includes('BrandMisuseTradingStandardsUser')) {
        next('/brand-misuse-intel');
      } else {
        next();
      }
    });
  } else {
    next();
  }
});

router.afterEach(() => {
  store.commit('setIsBusy', false, { root: true });
});

export default router;
